import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError } from "./api";
import { logger } from "./logger";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = unknown (loading), false = logged out, object = logged in
  const [user, setUser] = useState(null);

  useEffect(() => {
    // CRITICAL: If returning from the Google OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      return;
    }
    let mounted = true;
    api
      .get("/auth/me")
      .then((r) => mounted && setUser(r.data))
      .catch(() => mounted && setUser(false));
    return () => {
      mounted = false;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }, []);

  const register = useCallback(async (name, email, password) => {
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // best-effort: server may already have cleared the cookie; log only
      logger.warn("logout request failed:", err?.message || err);
    }
    setUser(false);
  }, []);

  // Memoized so consumers only re-render when auth state actually changes,
  // not on every AuthProvider render.
  const value = useMemo(
    () => ({ user, login, register, logout, setUser }),
    [user, login, register, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
